import { ArrowLeft, Bell, Save, Send } from 'lucide-react'
import { previewDay } from '../lib/preview'
import { saoPauloParts } from '../lib/schedule'
import type { Category, EventOccurrence, EventRun, EventSnapshot, PlaceFaq, Producer } from '../types'
import { EventPreview } from './EventPreview'

type Props = {
  // Both as they are saved (normalized); no original for a new event.
  snapshot: EventSnapshot
  original: EventSnapshot | null
  producers: Producer[]
  themes?: { slug: string; name: string }[]
  categories?: Category[]
  // The questions of its places changed in the editor (PRD.md, item 99).
  placeFaqs: { changed: PlaceFaq[]; saved: PlaceFaq[]; nameOf: (placeId: string) => string }
  saving: boolean
  onBack: () => void
  onConfirm: () => void
}

type Change = { label: string; before?: string; after: string }
type Group = { title: string; changes: Change[] }

const live = <T extends { status: string }>(items: T[]) => items.filter((item) => item.status !== 'deleted')
const money = (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const yesNo = (value: boolean | undefined) => (value ? 'Sim' : 'Não')
const instant = (value: string | null) => (value ? new Date(value).getTime() : null)

const ageOf = (event: EventSnapshot['event']) => event.is_kid_friendly ? 'Todas as idades (evento infantil)'
  : event.age_min === null ? 'Não informada'
    : event.age_min === 0 ? 'Todas as idades' : `A partir de ${event.age_min} anos`
const priceOf = (run: EventRun) => run.price_label || (run.is_free ? 'Gratuito'
  : run.price_min !== null ? `A partir de ${money(run.price_min)}` : 'Não informado')
const ticketOf = (run: EventRun) => !run.is_free ? null
  : run.requires_ticket === true ? 'Precisa de ingresso' : run.requires_ticket === false ? 'Não precisa de ingresso' : 'Não informado'
const sessionOf = (item: EventOccurrence) => {
  const start = saoPauloParts(item.starts_at).time
  const end = item.ends_at ? saoPauloParts(item.ends_at).time : null
  const when = item.is_all_day ? 'dia inteiro' : end ? `${start} – ${end}` : start
  return `${previewDay(item.starts_at)} · ${when}${item.status === 'canceled' ? ' (cancelado)' : ''}`
}
// One line a day, as a schedule reads: "Sex, 2 de out · 19:00, 21:00".
const daysOf = (occurrences: EventOccurrence[]) => {
  const days = new Map<string, string[]>()
  for (const item of [...live(occurrences)].sort((left, right) => left.starts_at.localeCompare(right.starts_at))) {
    const day = previewDay(item.starts_at)
    days.set(day, [...(days.get(day) ?? []), sessionOf(item).slice(day.length + 3)])
  }
  return [...days].map(([day, times]) => `${day} · ${times.join(', ')}`).join('\n')
}
const count = (value: number, one: string, many: string) => `${value} ${value === 1 ? one : many}`

// What a place of the event says, in the form's order.
function runRows(run: EventRun, names: (ids: string[]) => string): Change[] {
  return [
    { label: 'Perfis', after: names(run.credits ?? []) || 'Nenhum' },
    { label: 'Preço', after: priceOf(run) },
    ...(ticketOf(run) ? [{ label: 'Ingresso', after: ticketOf(run)! }] : []),
    { label: 'Link dos ingressos', after: run.ticket_url_source || 'Sem link' },
    { label: count(live(run.occurrences).length, 'horário', 'horários'), after: daysOf(run.occurrences) || 'Nenhum' },
  ]
}

// What changed since the event opened, as GitHub lists a change before it is
// confirmed: each field before and after, and each place's sessions added,
// removed or moved.
function changesOf(current: EventSnapshot, original: EventSnapshot, names: (ids: string[]) => string, themeNames: (slugs?: string[]) => string,
  categoryNames: (slugs?: string[]) => string): Group[] {
  const groups: Group[] = []
  const field = (label: string, before: string, after: string): Change[] => (before === after ? [] : [{ label, before, after }])
  const [now, then] = [current.event, original.event]
  // The photo and each framing say only what they became.
  const framed = (label: string, before: [number | null | undefined, number | null | undefined], after: typeof before): Change[] =>
    (before[0] ?? null) === (after[0] ?? null) && (before[1] ?? null) === (after[1] ?? null) ? []
      : [{ label, after: after[0] === null || after[0] === undefined ? 'Voltou ao centro' : 'Ajustado' }]
  const about = [
    ...field('Título', then.title, now.title),
    ...field('Categoria', then.category, now.category),
    ...field('Tema', themeNames(original.theme_slugs), themeNames(current.theme_slugs)),
    ...field('Também aparece em', categoryNames(original.extra_category_slugs), categoryNames(current.extra_category_slugs)),
    ...field('Descrição', then.description || '—', now.description || '—'),
    ...(then.image_url === now.image_url ? [] : [{ label: 'Foto', after: now.image_url ? 'Foto nova' : 'Sem foto' }]),
    ...framed('Enquadramento do card', [then.image_focus_x, then.image_focus_y], [now.image_focus_x, now.image_focus_y]),
    ...framed('Enquadramento da página', [then.image_page_focus_x, then.image_page_focus_y], [now.image_page_focus_x, now.image_page_focus_y]),
    ...field('Classificação etária', ageOf(then), ageOf(now)),
    ...field('Evento infantil', yesNo(then.is_kid_friendly), yesNo(now.is_kid_friendly)),
    ...field('Destaque no mapa', yesNo(then.map_featured), yesNo(now.map_featured)),
  ]
  if (about.length) groups.push({ title: 'Sobre o evento', changes: about })

  const runsBefore = new Map(original.runs.map((run) => [run.id, run]))
  for (const run of current.runs) {
    const before = runsBefore.get(run.id)
    if (!before || before.status === 'deleted') {
      if (run.status !== 'deleted') groups.push({ title: `Novo local: ${run.place_name}`, changes: runRows(run, names) })
      continue
    }
    if (run.status === 'deleted') {
      groups.push({ title: `Local removido: ${before.place_name}`, changes: [{ label: 'Horários', after: `${count(live(before.occurrences).length, 'horário sai', 'horários saem')} junto` }] })
      continue
    }
    const changes = [
      ...field('Local', before.place_name, run.place_name),
      ...(before.status !== run.status ? [{ label: 'Neste local', after: run.status === 'canceled' ? 'Cancelado' : 'Reativado' }] : []),
      ...field('Perfis', names(before.credits ?? []) || 'Nenhum', names(run.credits ?? []) || 'Nenhum'),
      ...field('Preço', priceOf(before), priceOf(run)),
      ...field('Ingresso', ticketOf(before) ?? '—', ticketOf(run) ?? '—'),
      ...field('Link dos ingressos', before.ticket_url_source || 'Sem link', run.ticket_url_source || 'Sem link'),
    ]
    const sessionsBefore = new Map(before.occurrences.map((item) => [item.id, item]))
    const added = run.occurrences.filter((item) => item.status !== 'deleted' && (!sessionsBefore.get(item.id) || sessionsBefore.get(item.id)!.status === 'deleted'))
    const removed = before.occurrences.filter((item) => item.status !== 'deleted'
      && (run.occurrences.find((next) => next.id === item.id)?.status ?? 'deleted') === 'deleted')
    if (added.length) changes.push({ label: count(added.length, 'horário novo', 'horários novos'), after: daysOf(added) })
    if (removed.length) changes.push({ label: count(removed.length, 'horário removido', 'horários removidos'), after: daysOf(removed) })
    for (const item of live(run.occurrences)) {
      const previous = sessionsBefore.get(item.id)
      if (!previous || previous.status === 'deleted') continue
      const moved = instant(previous.starts_at) !== instant(item.starts_at) || instant(previous.ends_at) !== instant(item.ends_at) || previous.is_all_day !== item.is_all_day
      if (moved || previous.status !== item.status) {
        const label = previous.status !== item.status ? (item.status === 'canceled' ? 'Horário cancelado' : 'Horário reativado') : 'Horário alterado'
        changes.push({ label, before: sessionOf({ ...previous, status: 'published' }), after: sessionOf({ ...item, status: 'published' }) })
      }
      const ticket = (value: EventOccurrence) => [value.price_label_override, value.price_min_override, value.ticket_url_override].join('|')
      if (ticket(previous) !== ticket(item)) changes.push({ label: 'Preço ou link de um horário', after: sessionOf(item) })
    }
    if (changes.length) groups.push({ title: `Local: ${run.place_name}`, changes })
  }

  const faqsBefore = new Map(original.faqs.map((faq) => [faq.id, faq]))
  const faqs: Change[] = []
  for (const faq of current.faqs) {
    const before = faqsBefore.get(faq.id)
    if (!before || before.status === 'deleted') { if (faq.status !== 'deleted') faqs.push({ label: 'Nova pergunta', after: faq.question }); continue }
    if (faq.status === 'deleted') { faqs.push({ label: 'Pergunta removida', after: before.question }); continue }
    if (before.question !== faq.question || before.answer !== faq.answer || before.event_run_id !== faq.event_run_id) {
      faqs.push({ label: 'Pergunta alterada', before: `${before.question}\n${before.answer}`, after: `${faq.question}\n${faq.answer}` })
    }
  }
  const order = (value: EventSnapshot) => live(value.faqs).map((faq) => faq.id).filter((id) => live(current.faqs).some((faq) => faq.id === id) && live(original.faqs).some((faq) => faq.id === id)).join()
  if (!faqs.length && order(current) !== order(original)) faqs.push({ label: 'Ordem', after: 'As perguntas mudaram de ordem' })
  if (faqs.length) groups.push({ title: 'Perguntas e respostas', changes: faqs })
  return groups
}

// A place's questions change for every event there, so they come apart.
function placeFaqGroups({ changed, saved, nameOf }: Props['placeFaqs']): Group[] {
  const before = new Map(saved.map((faq) => [faq.id, faq]))
  const places = [...new Set(changed.map((faq) => faq.place_id))]
  return places.map((placeId) => {
    const changes: Change[] = []
    for (const faq of changed.filter((item) => item.place_id === placeId)) {
      const previous = before.get(faq.id)
      if (!previous) { if (faq.status !== 'deleted') changes.push({ label: 'Nova pergunta', after: `${faq.question}\n${faq.answer}` }); continue }
      if (faq.status === 'deleted') { changes.push({ label: 'Pergunta removida', after: previous.question }); continue }
      if (previous.question !== faq.question || previous.answer !== faq.answer) {
        changes.push({ label: 'Pergunta alterada', before: `${previous.question}\n${previous.answer}`, after: `${faq.question}\n${faq.answer}` })
      }
    }
    if (!changes.length) changes.push({ label: 'Ordem', after: 'As perguntas mudaram de ordem' })
    return { title: `Perguntas de ${nameOf(placeId)}, em todos os eventos de lá`, changes }
  })
}

function Rows({ changes }: { changes: Change[] }) {
  return (
    <dl className="review-rows">
      {changes.map((change, index) => (
        <div key={`${change.label}-${index}`}>
          <dt>{change.label}</dt>
          <dd>
            {change.before !== undefined && <span className="review-before">{change.before}</span>}
            <span>{change.after}</span>
          </dd>
        </div>
      ))}
    </dl>
  )
}

// The step before publishing or saving (PRD.md, item 46), as Eventbrite's
// "Revisar e publicar": a new event in full, as it goes to the app; an edit,
// only what changed.
export function EventReview({ snapshot, original, producers, themes = [], categories = [], placeFaqs, saving, onBack, onConfirm }: Props) {
  const names = (ids: string[]) => ids.map((id) => producers.find((item) => item.id === id)?.name ?? 'Perfil removido').join(', ')
  const themeNames = (slugs: string[] = []) => [...slugs].sort().map(slug => themes.find(theme => theme.slug === slug)?.name ?? slug).join(', ') || 'Nenhum'
  const categoryNames = (slugs: string[] = []) => [...slugs].sort().map((slug) => {
    const category = categories.find((item) => item.slug === slug)
    return category?.display_name || category?.name || slug
  }).join(', ') || 'Nenhuma'
  const { event } = snapshot
  const groups = [...(original ? changesOf(snapshot, original, names, themeNames, categoryNames) : [
    {
      title: 'Sobre o evento',
      changes: [
        { label: 'Título', after: event.title },
        { label: 'Categoria', after: event.category },
        { label: 'Tema', after: themeNames(snapshot.theme_slugs) },
        ...(snapshot.extra_category_slugs?.length ? [{ label: 'Também aparece em', after: categoryNames(snapshot.extra_category_slugs) }] : []),
        { label: 'Descrição', after: event.description || '—' },
        { label: 'Classificação etária', after: ageOf(event) },
        { label: 'Evento infantil', after: yesNo(event.is_kid_friendly) },
        { label: 'Destaque no mapa', after: yesNo(event.map_featured) },
      ],
    },
    ...live(snapshot.runs).map((run, index) => ({
      title: `Local ${index + 1}: ${run.place_name}${run.status === 'canceled' ? ' (cancelado)' : ''}`,
      changes: runRows(run, names),
    })),
    ...(live(snapshot.faqs).length ? [{
      title: 'Perguntas e respostas',
      changes: live(snapshot.faqs).map((faq) => ({
        label: faq.event_run_id ? snapshot.runs.find((run) => run.id === faq.event_run_id)?.place_name ?? 'Um local' : 'Todos os locais',
        after: `${faq.question}\n${faq.answer}`,
      })),
    }] : []),
  ]), ...placeFaqGroups(placeFaqs)]
  // Moving or canceling a session tells who put it in their calendar
  // (docs/notifications.md).
  const notifies = groups.some((group) => group.changes.some((change) => ['Horário alterado', 'Horário cancelado', 'Neste local'].includes(change.label) && change.after !== 'Reativado'))
  const confirm = original ? 'Salvar' : 'Publicar'

  return (
    <section className="event-review" aria-label={original ? 'Revisar alterações' : 'Revisar e publicar'}>
      <div className="editor-heading">
        <button className="icon-button" onClick={onBack} disabled={saving} aria-label="Voltar e editar"><ArrowLeft size={20} /></button>
        <div>
          <h1>{original ? 'Revisar alterações' : 'Revisar e publicar'}</h1>
          <p>{original ? `O que muda em “${event.title}” ao salvar.` : 'Tudo o que vai para o app. Para corrigir algo, volte e edite.'}</p>
        </div>
      </div>

      <div className="editor-layout">
        <div className="editor-main">
          {groups.length ? groups.map((group) => (
            <section className="panel review-group" key={group.title}>
              <h2>{group.title}</h2>
              <Rows changes={group.changes} />
            </section>
          )) : <section className="panel review-group"><h2>Nenhuma alteração</h2><p>O evento está como foi aberto.</p></section>}
          {notifies && <div className="notice notice--soft" role="note"><Bell size={16} /><span>Mudanças de horário e cancelamentos avisam quem pôs o horário na agenda.</span></div>}
        </div>
        <EventPreview snapshot={snapshot} readOnly onFocus={() => {}} />
      </div>

      <div className="editor-footer" role="region" aria-label="Revisão">
        <span>{original ? `${groups.length ? count(groups.reduce((total, group) => total + group.changes.length, 0), 'alteração', 'alterações') : 'Nada para salvar'}` : 'Confira antes de publicar'}</span>
        <div>
          <button className="button button--secondary" disabled={saving} onClick={onBack}>Voltar e editar</button>
          <button className="button button--primary" disabled={saving || (Boolean(original) && !groups.length)} onClick={onConfirm}>
            {original ? <Save size={18} /> : <Send size={18} />} {saving ? 'Salvando…' : confirm}
          </button>
        </div>
      </div>
    </section>
  )
}
